import { z } from 'zod'

import { dateUtil, isNull, isSome } from '../../util'
import { csvColumnLabelFor } from '../csvColumnLabel'

export const millsheetCsvColumnMapper = {
  constructionName: '工事名称',
  constructionArea: '工区',
  member: '節',
  buildingPart: '部位',
  steelType: '規格',
  sectionType: '形状',
  manufacturerName: 'メーカー',
  tradingCompany: '商社',
  issuedDate: '発行日',
  no: '証明書番号',
  orderNumber: '注文番号',
  innerNo: '内部番号',
  customer: '需要家',
  size: '寸法',
  thicknessDiameter: '厚・径',
  endorsementCompany1: '裏書会社1',
  endorsementDate1: '裏書日1',
  endorsementCompany2: '裏書会社2',
  endorsementDate2: '裏書日2',
  endorsementCompany3: '裏書会社3',
  endorsementDate3: '裏書日3',
  endorsementCompany4: '裏書会社4',
  endorsementDate4: '裏書日4',
  endorsementCompany5: '裏書会社5',
  endorsementDate5: '裏書日5',
  endorsementCompany6: '裏書会社6',
  endorsementDate6: '裏書日6',
  length: '長さ',
  quantity: '数量',
  weightBeforeProcessing: '重量',
  // weightAfterProcessing: '加工後重量',
  heatNumber: '製鋼番号',
  individualNumber: '個体番号',
  pageNumber: '掲載ページ',
}

type EndorsementDateKey = `endorsementDate${1 | 2 | 3 | 4 | 5 | 6}`

// 番号なしの旧ヘッダー。裏書を1階層で運用していた頃のCSVをそのまま取り込めるよう、階層1の別名として受け付ける
const LEGACY_ENDORSEMENT_COMPANY_HEADER = '裏書会社'
const LEGACY_ENDORSEMENT_DATE_HEADER = '裏書日'

// 値が入っている方を採用し、両方に値があれば番号付きを優先する。
// 「裏書会社」に値があり「裏書会社1」が空欄の CSV で、旧ヘッダーの値が消えるのを防ぐため
function pickEndorsementCell(
  record: Record<string, string | number | null>,
  numberedHeader: string,
  legacyHeader: string,
) {
  const hasValue = (v: string | number | null | undefined) => isSome(v) && String(v).trim() !== ''
  const numbered = record[numberedHeader]
  return hasValue(numbered) || !hasValue(record[legacyHeader]) ? numbered : record[legacyHeader]
}

// 裏書会社（スポット品にのみ存在）。後方互換のため optional。空欄は null
const endorsementCompanySchema = z
  .string()
  .optional()
  .transform((x) => {
    if (isNull(x)) {
      return null
    }
    const trimmed = x.trim()
    return trimmed === '' ? null : trimmed
  })

// 裏書日。後方互換のため optional。空欄なら null、形式エラーは下流で個別にハンドリング
const endorsementDateSchema = z.string().optional()

// 原本PDFの掲載ページ（1始まり）。列なし・空欄は null。全角数字は半角へ寄せて受け付ける。
// DB の Int4 に収まらない値を保存時エラー（行番号なし）にしないため、ここで上限も弾く
const PAGE_NUMBER_MAX = 2_147_483_647
const pageNumberSchema = z.preprocess(
  (v) => v ?? '',
  z.string().transform((x, ctx) => {
    const normalized = x.normalize('NFKC').trim()
    if (normalized === '') {
      return null
    }
    const parsed = /^[0-9]+$/.test(normalized) ? Number(normalized) : Number.NaN
    if (!Number.isSafeInteger(parsed) || parsed < 1 || parsed > PAGE_NUMBER_MAX) {
      ctx.addIssue({ code: 'custom', message: '1以上の整数で入力してください。' })
      return z.NEVER
    }
    return parsed
  }),
)

// value は CSVレコードの生値（string | number | null）または zod パース後の文字列のみで、
// オブジェクトが渡ることはない。unknown のままだと String(value) が
// no-base-to-string に引っかかるため、実際に渡り得る型まで狭める
function formatValidationMessage({
  label,
  reason,
  value,
}: {
  label: string
  reason: string
  value: string | number | null | undefined
}) {
  const normalizedReason = reason.endsWith('。') ? reason : `${reason}。`
  const inputValue = isNull(value) ? '' : String(value)
  const inputValueMessage = inputValue.trim() === '' ? '' : `（入力値: ${inputValue}）`
  return `${label}: ${normalizedReason}${inputValueMessage}`
}

export function validateAndNormalizeMillsheetCsvRecords({
  record,
  index,
}: {
  record: Record<string, string | number | null>
  index: number
}) {
  // すべて空行だったらスキップ
  if (Object.values(record).every((x) => x === '')) {
    return {
      type: 'rebar' as const,
      success: false as const,
      isEmptyLine: true,
      message: `空行です。`,
      record,
      index,
    }
  }

  // TODO: keysのvalidation

  // 必要なデータのみを一旦抽出する
  const data = {
    manufacturerName: record[millsheetCsvColumnMapper.manufacturerName],
    tradingCompany: record[millsheetCsvColumnMapper.tradingCompany],
    customer: record[millsheetCsvColumnMapper.customer],
    constructionArea: record[millsheetCsvColumnMapper.constructionArea],
    member: record[millsheetCsvColumnMapper.member],
    buildingPart: record[millsheetCsvColumnMapper.buildingPart],
    constructionName: record[millsheetCsvColumnMapper.constructionName],
    issuedDate: record[millsheetCsvColumnMapper.issuedDate],
    no: record[millsheetCsvColumnMapper.no],
    orderNumber: record[millsheetCsvColumnMapper.orderNumber],
    innerNo: record[millsheetCsvColumnMapper.innerNo],
    steelType: record[millsheetCsvColumnMapper.steelType],
    sectionType: record[millsheetCsvColumnMapper.sectionType],
    size: record[millsheetCsvColumnMapper.size],
    thicknessDiameter: record[millsheetCsvColumnMapper.thicknessDiameter],
    endorsementCompany1: pickEndorsementCell(
      record,
      millsheetCsvColumnMapper.endorsementCompany1,
      LEGACY_ENDORSEMENT_COMPANY_HEADER,
    ),
    endorsementDate1: pickEndorsementCell(
      record,
      millsheetCsvColumnMapper.endorsementDate1,
      LEGACY_ENDORSEMENT_DATE_HEADER,
    ),
    endorsementCompany2: record[millsheetCsvColumnMapper.endorsementCompany2],
    endorsementDate2: record[millsheetCsvColumnMapper.endorsementDate2],
    endorsementCompany3: record[millsheetCsvColumnMapper.endorsementCompany3],
    endorsementDate3: record[millsheetCsvColumnMapper.endorsementDate3],
    endorsementCompany4: record[millsheetCsvColumnMapper.endorsementCompany4],
    endorsementDate4: record[millsheetCsvColumnMapper.endorsementDate4],
    endorsementCompany5: record[millsheetCsvColumnMapper.endorsementCompany5],
    endorsementDate5: record[millsheetCsvColumnMapper.endorsementDate5],
    endorsementCompany6: record[millsheetCsvColumnMapper.endorsementCompany6],
    endorsementDate6: record[millsheetCsvColumnMapper.endorsementDate6],
    length: record[millsheetCsvColumnMapper.length],
    quantity: record[millsheetCsvColumnMapper.quantity],
    weightBeforeProcessing: record[millsheetCsvColumnMapper.weightBeforeProcessing],
    // weightAfterProcessing: record[millsheetCsvColumnMapper.weightAfterProcessing],
    heatNumber: record[millsheetCsvColumnMapper.heatNumber],
    individualNumber: record[millsheetCsvColumnMapper.individualNumber],
    pageNumber: record[millsheetCsvColumnMapper.pageNumber],
  }
  // schemaもハードコーディング
  const schema = z.object({
    manufacturerName: z.string(),
    tradingCompany: z.string().optional(),
    customer: z.string().optional(),
    constructionArea: z.string().optional(),
    member: z.string().optional(),
    buildingPart: z.string().optional(),
    constructionName: z.string().optional(),
    issuedDate: z.string(),
    // 証明書番号は「工事名称 × 証明書番号」でミルシートを一意識別するキーの一部であり、
    // 未入力だと一意化が破綻する（本番データにも未入力は存在しない）。そのため必須とし、
    // 未入力・空欄（前後空白のみを含む）は受け付けない。null/undefined も空文字に寄せて弾く。
    no: z.preprocess((v) => v ?? '', z.string().trim().min(1, { message: '必須項目です。' })),
    orderNumber: z.string().optional(),
    innerNo: z.string().optional(),
    steelType: z.string(),
    sectionType: z.string(),
    size: z.string(),
    // 後方互換のため optional。未指定または空文字なら null として扱い、BPO が後から手入力する想定
    thicknessDiameter: z
      .string()
      .optional()
      .transform((x) => {
        if (isNull(x)) {
          return null
        }
        const trimmed = x.trim()
        return trimmed === '' ? null : trimmed
      }),
    endorsementCompany1: endorsementCompanySchema,
    endorsementDate1: endorsementDateSchema,
    endorsementCompany2: endorsementCompanySchema,
    endorsementDate2: endorsementDateSchema,
    endorsementCompany3: endorsementCompanySchema,
    endorsementDate3: endorsementDateSchema,
    endorsementCompany4: endorsementCompanySchema,
    endorsementDate4: endorsementDateSchema,
    endorsementCompany5: endorsementCompanySchema,
    endorsementDate5: endorsementDateSchema,
    endorsementCompany6: endorsementCompanySchema,
    endorsementDate6: endorsementDateSchema,
    length: z.string().optional(),
    // quantityは数値の文字列（カンマ、小数点を許す）
    quantity: z.string().transform((x, ctx) => {
      if (x.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: '必須項目です。',
        })
        return z.NEVER
      }
      if (!/^[0-9,.]+$/.test(x)) {
        ctx.addIssue({
          code: 'custom',
          message: '数値で入力してください。',
        })
        return z.NEVER
      }
      return Number(x.replace(/,/g, ''))
    }),
    weightBeforeProcessing: z
      .string()
      .transform((x, ctx) => {
        const trimmed = x.trim()
        if (trimmed === '') {
          return null
        }
        if (!/^[0-9,]+$/.test(trimmed)) {
          ctx.addIssue({
            code: 'custom',
            message: '整数で入力してください。',
          })
          return z.NEVER
        }
        const parsed = Number(trimmed.replace(/,/g, ''))
        if (!Number.isInteger(parsed)) {
          ctx.addIssue({
            code: 'custom',
            message: '整数で入力してください。',
          })
          return z.NEVER
        }
        return parsed
      })
      .nullable(),
    // weightAfterProcessing: z
    //   .string()
    //   .regex(/^[0-9,.]*$/, { message: '数値で入力してください。' })
    //   .optional()
    //   .transform((x) => {
    //     if (isNull(x) || !isPresent(x)) {
    //       return undefined
    //     }
    //     return Number(x.replace(/,/g, ''))
    //   }),
    heatNumber: z.string(),
    individualNumber: z.string().optional(),
    pageNumber: pageNumberSchema,
  })
  const parsed = schema.safeParse(data)

  if (!parsed.success) {
    return {
      type: 'rebar' as const,
      success: false as const,
      message: parsed.error.issues
        .map((x) => {
          const label = csvColumnLabelFor(millsheetCsvColumnMapper, x.path.join(''))
          const value = data[x.path.join('') as keyof typeof data]
          const reason = x.code === 'invalid_type' && isNull(value) ? '必須項目です。' : x.message
          return formatValidationMessage({ label, reason, value })
        })
        .join(' '),
      record,
      index,
    }
  }

  const dateParsed = dateUtil.parseDateFormatString(parsed.data.issuedDate)
  if (isNull(dateParsed)) {
    return {
      type: 'rebar' as const,
      success: false as const,
      message: formatValidationMessage({
        label: millsheetCsvColumnMapper.issuedDate,
        reason: 'YYYY/MM/DD形式で入力してください。',
        value: parsed.data.issuedDate,
      }),
      record,
      index,
    }
  }

  // 裏書日は optional。値があり、かつ形式不正なら明示的にエラー扱い
  const parseEndorsementDate = (key: EndorsementDateKey, raw: string | undefined) => {
    const trimmed = raw?.trim() ?? ''
    return { key, trimmed, parsed: trimmed === '' ? null : dateUtil.parseDateFormatString(trimmed) }
  }
  const endorsementDateResults = {
    endorsementDate1: parseEndorsementDate('endorsementDate1', parsed.data.endorsementDate1),
    endorsementDate2: parseEndorsementDate('endorsementDate2', parsed.data.endorsementDate2),
    endorsementDate3: parseEndorsementDate('endorsementDate3', parsed.data.endorsementDate3),
    endorsementDate4: parseEndorsementDate('endorsementDate4', parsed.data.endorsementDate4),
    endorsementDate5: parseEndorsementDate('endorsementDate5', parsed.data.endorsementDate5),
    endorsementDate6: parseEndorsementDate('endorsementDate6', parsed.data.endorsementDate6),
  }
  const invalidEndorsementDates = Object.values(endorsementDateResults).filter(
    (x) => x.trimmed !== '' && isNull(x.parsed),
  )
  if (invalidEndorsementDates.length > 0) {
    return {
      type: 'rebar' as const,
      success: false as const,
      message: invalidEndorsementDates
        .map((x) =>
          formatValidationMessage({
            label: millsheetCsvColumnMapper[x.key],
            reason: 'YYYY/MM/DD形式で入力してください。',
            value: x.trimmed,
          }),
        )
        .join(' '),
      record,
      index,
    }
  }

  const result = {
    type: 'rebar' as const,
    ...parsed.data,
    rank: index + 1,
    issuedDate: dateParsed.date,
    endorsementDate1: endorsementDateResults.endorsementDate1.parsed?.date ?? null,
    endorsementDate2: endorsementDateResults.endorsementDate2.parsed?.date ?? null,
    endorsementDate3: endorsementDateResults.endorsementDate3.parsed?.date ?? null,
    endorsementDate4: endorsementDateResults.endorsementDate4.parsed?.date ?? null,
    endorsementDate5: endorsementDateResults.endorsementDate5.parsed?.date ?? null,
    endorsementDate6: endorsementDateResults.endorsementDate6.parsed?.date ?? null,
    raw: data,
  }

  return {
    type: 'rebar' as const,
    success: true as const,
    record,
    index,
    result,
    resultTitle: result.heatNumber,
  }
}
